import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  PageHeader,
  SearchFilterBar,
  DataTable,
  StatusChip,
  flattenPages,
} from '../components';
import { useTransfersPaginated } from '../hooks/useStockTransfers';
import { colors, formatDate } from '../constants';

export default function StockTransfers() {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useMemo(
    () => ({
      limit: 20,
      status: statusFilter || undefined,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [statusFilter, search, dateFrom, dateTo]
  );

  const query = useTransfersPaginated(params);
  const items = flattenPages(query);

  const columns = [
    {
      key: 'uuid',
      label: 'Transfer',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.navy }}>
          {row.uuid.slice(0, 8)}
        </Typography>
      ),
    },
    {
      key: 'route',
      label: 'Route',
      render: (row) => (
        <Typography variant="caption" sx={{ color: colors.gray500 }}>
          {(row.fromWarehouseName || row.fromWarehouseUuid || '-')} →{' '}
          {row.toWarehouseName || row.toWarehouseUuid}
        </Typography>
      ),
    },
    { key: 'items', label: 'Items', align: 'right', render: (row) => row.items?.length || 0 },
    { key: 'createdAt', label: 'Date', render: (row) => formatDate(row.createdAt) },
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Transfers"
        actionLabel="New Transfer"
        actionIcon={<AddIcon />}
        onAction={() => history.push('/inventory-management/stock-transfers/new')}
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search transfers..."
        filters={[
          {
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'draft', label: 'Draft' },
              { value: 'in_transit', label: 'In Transit' },
              { value: 'received', label: 'Received' },
            ],
          },
        ]}
      >
        <Stack direction="row" spacing={1}>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ backgroundColor: '#fff' }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ backgroundColor: '#fff' }} />
        </Stack>
      </SearchFilterBar>

      <DataTable
        columns={columns}
        rows={items}
        loading={query.isLoading}
        onRowClick={(row) => history.push(`/inventory-management/stock-transfers/${row.uuid}`)}
        emptyTitle="No transfers found"
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        fetchNextPage={query.fetchNextPage}
      />
    </>
  );
}
